import { useState, useEffect, useRef, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { PaletteUtils } from '@shared/utils';
import { DatePickerPopup } from './DatePickerPopup';
import ClearIcon from '@assets/icons/datePicker/clear.svg';
import ArrowIcon from '@assets/icons/datePicker/dropDown.svg';

const DATE_FORMAT = 'ddd, DD MMM';
const TRANSITION_DURATION = 400;

export interface SelectedDays {
  from: string | undefined;
  to: string | undefined;
}

interface DatePickerProps {
  isMobile: boolean;
  range: SelectedDays;
  iconMode?: boolean;
  icon?: ReactNode;
  openIcon?: ReactNode;
  className?: string;
  onConfirm: (range: SelectedDays | null) => void;
}

export const DatePicker = (props: DatePickerProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<SelectedDays | null>(null);
  const [confirmed, setConfirmed] = useState<boolean>(true);
  const node = useRef<HTMLDivElement>(null);
  const nodeStandard = useRef<HTMLDivElement>(null);
  const popup = useRef<{
    adjustPosition: (rect: DOMRect) => void;
  }>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (node.current && !node.current.contains(e.target as Node)) closeup();
    };

    document.body.addEventListener('click', handleOutsideClick, false);

    return () => {
      document.body.removeEventListener('click', handleOutsideClick, false);
    };
  }, []);

  useEffect(() => {
    if (!open && !confirmed) setConfirmed(true);
  }, [open, confirmed]);

  useEffect(() => {
    if (open) adjustPopupPosition();
  }, [open]);

  const dropdown = (): void => setOpen(true);
  const closeup = (): void => setOpen(false);
  const adjustPopupPosition = (): void => {
    if (nodeStandard.current && popup.current) {
      const spanElement = nodeStandard.current.querySelector('span');
      if (spanElement) {
        const rect = spanElement.getBoundingClientRect();
        popup.current.adjustPosition(rect);
      }
    }
  };
  const handleDayClick = (days: SelectedDays): void => {
    setSelectedDays(days);
    setConfirmed(false);
  };
  const handleConfirm = () => {
    props.onConfirm(selectedDays);
    closeup();
    setConfirmed(true);
  };
  const clear = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDays(null);
    setConfirmed(true);
    props.onConfirm(null);
  };

  const haveData = selectedDays && selectedDays.to;
  const text = useMemo(() => {
    if (!haveData) return t('datePicker.placeholders.pickDateRange');

    const locale = i18n.language || 'en';
    const format = (date?: string) => (date ? dayjs(date).locale(locale).format(DATE_FORMAT) : '');

    const from = format(selectedDays.from);
    const to = format(selectedDays.to);

    return from && to ? `${from} - ${to}` : from || to;
  }, [selectedDays?.from, selectedDays?.to, i18n.language, t]);

  return (
    <div ref={node} className={clsx('inline-block select-none before:inline-block before:h-full before:align-middle', props.className)}>
      {!props.iconMode ? (
        <div
          ref={nodeStandard}
          className='cursor-pointer inline-block h-full align-middle before:inline-block before:h-full before:align-middle'
          onClick={dropdown}
        >
          <span
            className={clsx(
              'flex items-center gap-2 mb-1 max-[840px]:mb-4 font-bold',
              PaletteUtils.isBordierPalette
                ? 'max-[840px]:text-[#dadada] text-base tracking-[0.3px]'
                : 'text-[#dadada] text-[13px] uppercase tracking-[-0.1px]',
            )}
          >
            {text}
            {haveData && (
              <div className='cursor-pointer inline-block mr-1.5 align-middle w-5' onClick={clear}>
                <ClearIcon className={clsx(PaletteUtils.isBordierPalette ? 'text-black' : 'text-[#dadada]')} />
              </div>
            )}
            <ArrowIcon className={clsx('w-[11px] h-[9px]', PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#e5c496]')} />
          </span>
          {props.openIcon}
        </div>
      ) : (
        <div className='cursor-pointer h-full inline-block text-center align-middle w-full' onClick={dropdown}>
          {props.icon}
        </div>
      )}
      <CSSTransition in={open} nodeRef={popupRef} timeout={TRANSITION_DURATION} unmountOnExit classNames='datePicker'>
        <div className='h-full left-0 flex pointer-events-none absolute top-0 w-full z-[1000]' ref={popupRef}>
          <DatePickerPopup
            ref={popup}
            className='pointer-events-auto'
            onCancel={closeup}
            isMobile={props.isMobile}
            range={selectedDays}
            onConfirm={handleConfirm}
            onDayClick={handleDayClick}
            open={open}
          />
        </div>
      </CSSTransition>
    </div>
  );
};
